import { tenantProfileService } from '@/services/tenant-profile.service';
import { aiModulesService } from '@/services/ai-modules.service';

/**
 * State shape:
 *   templates: [{ id, name, isDefault, ... }]  — summaries from the list endpoint
 *   currentTemplate: { id, name, categories: [{ id, name, entries: [...] }] }
 *     — full detail of the selected template, fetched on demand via `getTemplate`.
 *       The selected id is owned by the component; all category/entry CRUD here
 *       operates on `currentTemplate`.
 */

const initialState = () => ({
  templates: [],
  currentTemplate: null,
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

// Live AbortController for the in-flight import SSE stream. Kept outside Vuex
// state (not structured-clone-safe) like the evaluation-grid extraction job.
let activeImportController = null;

// --- helpers --------------------------------------------------------------

const findActiveTemplate = (state) => state.currentTemplate;

const findActiveCategory = (state, categoryId) => {
  const template = findActiveTemplate(state);
  return template && template.categories ? template.categories.find((c) => c.id === categoryId) : null;
};

// Map a template-detail payload from the API into the store's internal shape:
// API `categories[].fields[]` (key / altKey / value / language / source) become
// `categories[].entries[]` (title / aliases / value / language / translations / source),
// matching the shape produced by `addEntry`.
const normalizeEntry = (field, categoryId) => ({
  id: field.id,
  categoryId,
  title: field.key,
  aliases: field.altKey
    ? field.altKey
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
    : [],
  value: field.value,
  language: field.language || null,
  translations: [],
  source: field.source || 'manual',
});

// Fields sharing the same label (key) are the same logical field expressed in
// different languages. Group them into one entry: the first record is the
// primary value, the rest become other-language values (`translations`).
const normalizeCategory = (category) => {
  const byLabel = new Map();
  (category.fields || []).forEach((field) => {
    const existing = byLabel.get(field.key);
    if (existing) {
      existing.translations.push({ id: field.id, language: field.language || null, value: field.value });
      // Track every per-language record so the field can be fully deleted.
      existing.languageIds.push(field.id);
    } else {
      const entry = normalizeEntry(field, category.id);
      entry.languageIds = [field.id];
      byLabel.set(field.key, entry);
    }
  });
  return { id: category.id, name: category.name, entries: [...byLabel.values()] };
};

const normalizeTemplate = (template) => ({
  ...template,
  categories: (template.categories || []).map(normalizeCategory),
});

// --- actions --------------------------------------------------------------

const actions = {
  getTemplates({ commit }) {
    commit('PENDING');
    return tenantProfileService.getTemplates().then(
      (templates) => commit('SET_TEMPLATES', { templates }),
      (error) => commit('ERROR', error),
    );
  },
  // Fetch the full detail of the selected template into `currentTemplate`.
  getTemplate({ commit }, id) {
    commit('PENDING');
    return tenantProfileService.getTemplate(id).then(
      (template) => commit('SET_CURRENT_TEMPLATE_DETAIL', normalizeTemplate(template)),
      (error) => commit('ERROR', error),
    );
  },
  // Persist the template, refresh the list, and return the new id so the view
  // can switch to it.
  addTemplate({ commit, dispatch }, { name, isDefault = false }) {
    commit('PENDING');
    return tenantProfileService.createTemplate({ name, isDefault }).then(
      (newTemplateId) => dispatch('getTemplates').then(() => newTemplateId || null),
      (error) => {
        commit('ERROR', error);
        return null;
      },
    );
  },
  // Rename a template. The PUT payload also carries isDefault, so pass the
  // template's current flag through to avoid un-defaulting it.
  editTemplate({ commit, state }, { templateId, name }) {
    const template =
      state.templates.find((t) => t.id === templateId) ||
      (state.currentTemplate && state.currentTemplate.id === templateId ? state.currentTemplate : null);
    const isDefault = !!(template && template.isDefault);
    commit('PENDING');
    return tenantProfileService.updateTemplate(templateId, { name, isDefault }).then(
      () => {
        commit('EDIT_TEMPLATE', { templateId, name });
        return true;
      },
      (error) => {
        commit('ERROR', error);
        return false;
      },
    );
  },
  // Mark a template as the default. Re-fetches so the list and current detail
  // reflect the new default flags.
  setDefaultTemplate({ commit, dispatch, state }, templateId) {
    const template = state.templates.find((t) => t.id === templateId);
    const name = template ? template.name : state.currentTemplate && state.currentTemplate.name;
    commit('PENDING');
    return tenantProfileService.updateTemplate(templateId, { name, isDefault: true }).then(
      () => Promise.all([dispatch('getTemplates'), dispatch('getTemplate', templateId)]).then(() => true),
      (error) => {
        commit('ERROR', error);
        return false;
      },
    );
  },
  deleteTemplate({ commit }, templateId) {
    commit('PENDING');
    return tenantProfileService.deleteTemplate(templateId).then(
      () => {
        commit('DELETE_TEMPLATE', templateId);
        return true;
      },
      (error) => {
        commit('ERROR', error);
        return false;
      },
    );
  },
  // createCategory only returns the new id, so re-fetch the template to pick up
  // the full category.
  addCategory({ commit, dispatch, state }, name) {
    const templateId = state.currentTemplate && state.currentTemplate.id;
    if (!templateId) return Promise.resolve(false);
    commit('PENDING');
    return tenantProfileService.createCategory(templateId, { name }).then(
      () => dispatch('getTemplate', templateId).then(() => true),
      (error) => {
        commit('ERROR', error);
        return false;
      },
    );
  },
  editCategory({ commit, state }, { categoryId, name }) {
    const templateId = state.currentTemplate && state.currentTemplate.id;
    if (!templateId) return Promise.resolve(false);
    commit('PENDING');
    return tenantProfileService.updateCategory(templateId, categoryId, { name }).then(
      () => {
        commit('EDIT_CATEGORY', { categoryId, name });
        return true;
      },
      (error) => {
        commit('ERROR', error);
        return false;
      },
    );
  },
  deleteCategory({ commit, state }, categoryId) {
    const templateId = state.currentTemplate && state.currentTemplate.id;
    if (!templateId) return Promise.resolve(false);
    commit('PENDING');
    return tenantProfileService.deleteCategory(templateId, categoryId).then(
      () => {
        commit('DELETE_CATEGORY', categoryId);
        return true;
      },
      (error) => {
        commit('ERROR', error);
        return false;
      },
    );
  },
  // A field's value in each language is its own API record. Creating an entry
  // creates one record per language; afterwards we re-fetch so the store has
  // the server ids and grouping.
  addEntry({ commit, dispatch, state }, { categoryId, title, aliases, value, language, translations = [] }) {
    const templateId = state.currentTemplate && state.currentTemplate.id;
    if (!templateId) return Promise.resolve(false);
    const altKey = (aliases || []).join(', ');
    const records = [{ language, value }, ...translations].filter((r) => r.language);
    commit('PENDING');
    return Promise.all(
      records.map((r) =>
        tenantProfileService.createField(templateId, categoryId, {
          key: title,
          altKey,
          value: r.value,
          language: r.language,
          source: 'manual',
        }),
      ),
    ).then(
      () => dispatch('getTemplate', templateId).then(() => true),
      (error) => {
        commit('ERROR', error);
        return false;
      },
    );
  },
  // Reconcile a field's per-language records against the edited values: update
  // languages that already exist, create new ones, delete removed ones.
  editEntry({ commit, dispatch, state }, entry) {
    const templateId = state.currentTemplate && state.currentTemplate.id;
    if (!templateId) return Promise.resolve(false);
    const category = findActiveCategory(state, entry.categoryId);
    const original = category ? category.entries.find((e) => e.id === entry.id) : null;
    const originalIds = {};
    if (original) {
      if (original.language) originalIds[original.language] = original.id;
      (original.translations || []).forEach((t) => {
        if (t.language && t.id) originalIds[t.language] = t.id;
      });
    }
    const altKey = (entry.aliases || []).join(', ');
    const nextByLang = {};
    if (entry.language) nextByLang[entry.language] = entry.value;
    (entry.translations || []).forEach((t) => {
      if (t.language) nextByLang[t.language] = t.value;
    });
    const ops = [];
    Object.keys(nextByLang).forEach((lang) => {
      const payload = {
        key: entry.title,
        altKey,
        value: nextByLang[lang],
        language: lang,
        source: 'manual',
      };
      if (originalIds[lang]) {
        ops.push(tenantProfileService.updateField(templateId, entry.categoryId, originalIds[lang], payload));
      } else {
        ops.push(tenantProfileService.createField(templateId, entry.categoryId, payload));
      }
    });
    Object.keys(originalIds).forEach((lang) => {
      if (!(lang in nextByLang))
        ops.push(tenantProfileService.deleteField(templateId, entry.categoryId, originalIds[lang]));
    });
    commit('PENDING');
    return Promise.all(ops).then(
      () => dispatch('getTemplate', templateId).then(() => true),
      (error) => {
        commit('ERROR', error);
        return false;
      },
    );
  },
  // A field can span several per-language records, and the API deletes one
  // record at a time. Delete every record of every selected field; a field is
  // removed locally only once all of its records are gone.
  deleteEntries({ commit, state }, { categoryId, ids }) {
    const templateId = state.currentTemplate && state.currentTemplate.id;
    if (!templateId) return Promise.resolve(false);
    commit('PENDING');
    const category = (state.currentTemplate.categories || []).find((c) => c.id === categoryId);
    const entries = category ? category.entries.filter((e) => ids.includes(e.id)) : [];
    const targets = entries.flatMap((e) =>
      (e.languageIds && e.languageIds.length ? e.languageIds : [e.id]).map((fieldId) => ({ entryId: e.id, fieldId })),
    );
    return Promise.all(
      targets.map((t) =>
        tenantProfileService.deleteField(templateId, categoryId, t.fieldId).then(
          () => true,
          () => false,
        ),
      ),
    ).then((results) => {
      const failedEntryIds = new Set(targets.filter((_, i) => !results[i]).map((t) => t.entryId));
      const deletedEntryIds = [...new Set(targets.map((t) => t.entryId))].filter((id) => !failedEntryIds.has(id));
      if (deletedEntryIds.length) commit('DELETE_ENTRIES', { categoryId, ids: deletedEntryIds });
      const allDeleted = failedEntryIds.size === 0;
      if (!allDeleted) commit('ERROR', new Error('Some fields could not be deleted'));
      return allDeleted;
    });
  },
  // Import fields from uploaded documents via the AI modules service. The POST
  // queues a job; we then watch it over SSE (like the evaluation-grid
  // extraction). On completion the import has created a new default template,
  // so we refresh the templates list and resolve { templateId, count } so the
  // view can switch to it (null on failure/cancel).
  extractFromDocuments({ commit, dispatch }, { files = [] }) {
    if (activeImportController) activeImportController.abort();
    const controller = new AbortController();
    activeImportController = controller;
    const { signal } = controller;
    const clear = () => {
      if (activeImportController === controller) activeImportController = null;
    };

    commit('PENDING');
    return aiModulesService.importTenantProfile(files, signal).then(
      (job) => {
        const jobId = job && job.job_id;
        if (!jobId) {
          clear();
          commit('ERROR', new Error('Import job was not created'));
          return null;
        }
        // Watch the job's SSE stream until it completes/fails.
        return new Promise((resolve) => {
          aiModulesService.subscribeToJob(
            { jobId, signal },
            {
              onComplete: (data) => {
                const templateId = data && data.template_id;
                const count = data && data.meta ? data.meta.field_count : null;
                // The import added a new default template — refresh the list.
                dispatch('getTemplates').then(() => {
                  clear();
                  resolve({ templateId, count });
                });
              },
              onError: (err) => {
                if (!signal.aborted) commit('ERROR', err);
                clear();
                resolve(null);
              },
              onCancelled: () => {
                clear();
                resolve(null);
              },
            },
          );
        });
      },
      (error) => {
        clear();
        if (signal.aborted) return null;
        commit('ERROR', error);
        return null;
      },
    );
  },
  // Abort an in-flight import (e.g. when leaving the page).
  cancelExtraction() {
    if (activeImportController) activeImportController.abort();
    activeImportController = null;
  },
};

// --- mutations ------------------------------------------------------------

const mutations = {
  PENDING(state) {
    state.status = { error: null, pending: true, resolved: false, rejected: false };
  },
  ERROR(state, error) {
    state.status = { ...state.status, pending: false, rejected: true, error };
  },
  SET_TEMPLATES(state, { templates }) {
    state.templates = templates;
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  // Store the full detail of the active template (categories/entries).
  SET_CURRENT_TEMPLATE_DETAIL(state, template) {
    state.currentTemplate = template;
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  EDIT_TEMPLATE(state, { templateId, name }) {
    const template = state.templates.find((t) => t.id === templateId);
    if (template) template.name = name;
    if (state.currentTemplate && state.currentTemplate.id === templateId) state.currentTemplate.name = name;
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  DELETE_TEMPLATE(state, templateId) {
    state.templates = state.templates.filter((t) => t.id !== templateId);
    if (state.currentTemplate && state.currentTemplate.id === templateId) state.currentTemplate = null;
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  EDIT_CATEGORY(state, { categoryId, name }) {
    const category = findActiveCategory(state, categoryId);
    if (category) category.name = name;
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  DELETE_CATEGORY(state, categoryId) {
    const template = findActiveTemplate(state);
    if (template) template.categories = template.categories.filter((c) => c.id !== categoryId);
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  DELETE_ENTRIES(state, { categoryId, ids }) {
    const category = findActiveCategory(state, categoryId);
    if (category) category.entries = category.entries.filter((e) => !ids.includes(e.id));
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
};

// --- getters --------------------------------------------------------------

const allEntries = (categories) => categories.reduce((acc, c) => acc.concat(c.entries), []);

const getters = {
  templates: (state) => state.templates,
  currentTemplate: (state) => findActiveTemplate(state),
  categories: (state) => {
    const template = findActiveTemplate(state);
    return template && template.categories ? template.categories : [];
  },
  entries: (state) => {
    const template = findActiveTemplate(state);
    return template && template.categories ? allEntries(template.categories) : [];
  },
  entryCount: (state, getters) => getters.entries.length,
  aiEntryCount: (state, getters) => getters.entries.filter((e) => e.source === 'ai').length,
  pending: (state) => state.status.pending,
};

export const tenantProfile = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
